import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Field,
  MoneyInput,
  Notice,
  Select,
  TextInput,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useResource } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import { sites as loadSites } from '../../api';
import { guards, saveGuards, type GuardPolicy } from './api';
import type { LoyaltyTranslate } from './i18n';

/**
 * Ограничения начисления.
 *
 * spec-4 §4.6 требует, чтобы пределы настраивались без разработчика; движок их
 * честно применяет с первого дня, а менять их до сих пор можно было только
 * запросом в базу. Экран закрывает ровно это.
 *
 * Настройка живёт двумя уровнями: сетевая (`site_id IS NULL`) и объектная.
 * Объектная перекрывает сетевую целиком, а не по полям, — поэтому на экране
 * видно, какой набор сейчас действует, и переключение объекта перезагружает
 * форму, а не переносит введённые числа на соседний объект.
 */

export interface GuardsTabProps {
  readonly t: LoyaltyTranslate;
  readonly canManage: boolean;
  readonly onError: (message: string) => void;
}

const NETWORK = '';

export function GuardsTab({ t, canManage, onError }: GuardsTabProps) {
  const toast = useToast();
  const siteList = useResource(() => loadSites(), []);

  const [siteId, setSiteId] = useState<string>(NETWORK);
  const [draft, setDraft] = useState<GuardPolicy | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setDraft(null);
    guards(siteId || null)
      .then((policy) => {
        if (alive) setDraft(policy);
      })
      .catch((err: Error) => {
        if (alive) onError(errorText(err).message);
      });
    return () => {
      alive = false;
    };
  }, [siteId, onError]);

  async function submit(): Promise<void> {
    if (!draft) return;
    setBusy(true);
    try {
      const saved = await saveGuards({ ...draft, siteId: siteId || null });
      setDraft(saved);
      toast.ok(t('guards.saved'));
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  function patch(next: Partial<GuardPolicy>): void {
    setDraft((current) => (current ? { ...current, ...next } : current));
  }

  /** Ноль в поле предела означает «без предела» — так его читает движок. */
  function limit(value: number): string {
    return value === 0 ? t('guards.unlimited') : String(value);
  }

  return (
    <Card title={t('guards.title')}>
      <p className="mb-4 text-sm text-ink-muted">{t('guards.hint')}</p>

      <div className="mb-4 w-72">
        <Field label={t('guards.site')} hint={t('guards.siteHint')}>
          <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value={NETWORK}>{t('guards.network')}</option>
            {(siteList.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} · {s.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {!draft ? (
        <p className="text-sm text-ink-muted">{t('guards.loading')}</p>
      ) : (
        <>
          {siteId && draft.siteId === null ? (
            <Notice tone="info" title={t('guards.inherited')}>
              {t('guards.inheritedHint')}
            </Notice>
          ) : null}

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label={t('guards.perVisit')} hint={t('guards.perVisitHint')}>
              <TextInput
                inputMode="numeric"
                disabled={!canManage}
                value={String(draft.maxAccrualsPerVisit)}
                onChange={(e) => patch({ maxAccrualsPerVisit: Number(e.target.value) || 0 })}
              />
            </Field>

            <Field label={t('guards.perPeriod')} hint={t('guards.perPeriodHint')}>
              <TextInput
                inputMode="numeric"
                disabled={!canManage}
                value={String(draft.maxAccrualsPerPeriod)}
                onChange={(e) => patch({ maxAccrualsPerPeriod: Number(e.target.value) || 0 })}
              />
            </Field>

            <Field label={t('guards.periodDays')} hint={t('guards.periodDaysHint')}>
              <TextInput
                inputMode="numeric"
                disabled={!canManage}
                value={String(draft.periodDays)}
                onChange={(e) => patch({ periodDays: Number(e.target.value) || 0 })}
              />
            </Field>

            <Field label={t('guards.amount')} hint={t('guards.amountHint')}>
              <MoneyInput
                disabled={!canManage}
                valueMinor={draft.maxAmountPerPeriodMinor}
                onChangeMinor={(minor) => patch({ maxAmountPerPeriodMinor: minor ?? 0 })}
              />
            </Field>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <label className="flex items-start gap-2 text-sm text-ink">
              <Checkbox
                checked={draft.requireVehicleLink}
                disabled={!canManage}
                onChange={(e) => patch({ requireVehicleLink: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                {t('guards.requireVehicle')}
                <span className="block text-xs text-ink-muted">
                  {t('guards.requireVehicleHint')}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-ink">
              <Checkbox
                checked={draft.blockSelfReferral}
                disabled={!canManage}
                onChange={(e) => patch({ blockSelfReferral: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                {t('guards.blockSelfReferral')}
                <span className="block text-xs text-ink-muted">
                  {t('guards.blockSelfReferralHint')}
                </span>
              </span>
            </label>
          </div>

          <p className="mt-4 text-xs text-ink-muted">
            {t('guards.summary', {
              visit: limit(draft.maxAccrualsPerVisit),
              period: limit(draft.maxAccrualsPerPeriod),
              days: draft.periodDays,
            })}
          </p>

          {canManage ? (
            <div className="mt-4">
              <Button tone="primary" disabled={busy} onClick={() => void submit()}>
                {t('guards.save')}
              </Button>
            </div>
          ) : (
            <Notice tone="info">{t('guards.readonly')}</Notice>
          )}
        </>
      )}
    </Card>
  );
}
